/**
 * JSON Schema for the FlightCaseSettings interface.
 *
 * Note: This schema provides the basic structure.
 * Detailed schemas for nested types (CoverPaddingSettings, CutProperties, etc.)
 * should ideally be generated or defined separately and referenced here for full validation.
 */
import { z } from "zod";

// --- Placeholder Schemas for Nested Types ---
// TODO: Define these schemas based on their actual interfaces

const coverPaddingSettingsSchema = z
  .object({
    // Example placeholder properties
    paddingType: z.string().optional(),
    thickness: z.number().optional(),
  })
  .describe("Schema for CoverPaddingSettings needed.");

const cutPropertiesSchema = z
  .object({
    // Example placeholder properties
    shape: z.string().optional(),
    position: z.object({ x: z.number(), y: z.number() }).optional(),
  })
  .describe("Schema for CutProperties needed.");

const exteriorOptionsSchema = z
  .object({
    // Example placeholder properties
    panelColor: z.string().optional(),
    panelStrength: z.string().optional(), // or z.number()
    corners: z.string().optional(),
    profiles: z.string().optional(),
  })
  .describe("Schema for ExteriorOptions needed.");

const caseInteriorOptionsSchema = z
  .object({
    layout: z.string().optional(), // Example
  })
  .describe("Schema for CaseInteriorOptions needed.");

const rackInteriorOptionsSchema = z
  .object({
    rackUnits: z.number().optional(), // Example
  })
  .describe("Schema for RackInteriorOptions needed.");

const rack19InteriorOptionsSchema = z
  .object({
    depth: z.number().optional(), // Example
  })
  .describe("Schema for Rack19InteriorOptions needed.");

const variableOptionsSchema = z
  .object({
    // Example placeholder properties
    name: z.string().optional(),
    value: z.any().optional(),
  })
  .describe("Schema for VariableOptions needed.");

// --- Main FlightCaseSettings Schema ---

export const flightCaseSettingsSchema = z
  .object({
    // Properties from ProductSettings (assuming ProductSettings base schema if available)
    type: z.string(), // Ideally refine with z.enum(Object.values(LTProducts))
    version: z.string(),

    // FlightCaseSettings specific properties
    coverPaddings: z.record(z.string(), coverPaddingSettingsSchema),
    cuts: z.array(cutPropertiesSchema),
    exteriorOptions: exteriorOptionsSchema,
    interiorOptions: z.union([
      caseInteriorOptionsSchema,
      rackInteriorOptionsSchema,
      rack19InteriorOptionsSchema,
    ]),
    options: z.any(),
    variableOptions: z.array(variableOptionsSchema),
  })
  .strict(); // Use .strict() to disallow additional properties

// --- Schemas for Partial Updates ---
// You can create partial schemas easily using .partial()

export const partialFlightCaseSettingsSchema =
  flightCaseSettingsSchema.partial();

// Example: Schema for updating only exterior options
export const exteriorOptionsUpdateSchema = z.object({
  exteriorOptions: exteriorOptionsSchema,
});

// Example: Schema for updating only cover paddings
export const coverPaddingsUpdateSchema = z.object({
  coverPaddings: z.record(z.string(), coverPaddingSettingsSchema),
});

// Type alias for easier usage
export type FlightCaseSettingsSchemaType = z.infer<
  typeof flightCaseSettingsSchema
>;
